import type { CSSProperties, JSX } from 'react';

/*
 * Логотип FISHKA HUB — макет владельца «FISHKA HUB Logo» в Claude Design
 * (hub-logos.jsx, 07.10.2026; стиль BRASOL Monolith). Пропорции перенесены
 * из макета как есть, геометрия — в global.css (.fk-hub-tile / .fk-hub-word):
 *  - знак (Tile): плитка индиго #251D4A, скругление .22 (в шапке — .25, как
 *    в «Nav bar lockup»), буква H — Fraunces 900, .64 от плитки, цвет Paper
 *    #F4F1EA; оранжевая черта .18 × max(2px, .017) на .145 от низа;
 *  - надпись (Word): FISHKA (оранжевый) + HUB (индиго), Fraunces 900,
 *    -0.04em, межстрочный .88, зазор .22; подпись «знания и обучение сети».
 *    В макете подпись — Space Grotesk, но в нём нет кириллицы (в макете она
 *    и так рисовалась запасным шрифтом), поэтому у нас — Manrope 500.
 * В свёрнутом меню остаётся только знак H. Тот же знак — в favicon.svg и
 * иконках приложения (public/icons, буква там — контур из Fraunces).
 */

type Vars = CSSProperties & Record<`--${string}`, string>;

/** Знак H на плитке. `outline` — светлая плитка с индиговой рамкой (для тёмного фона). */
export function HubTile({ size = 44, radius, outline }: { size?: number; radius?: number; outline?: boolean }): JSX.Element {
  const style: Vars = { '--s': `${size}px` };
  if (radius != null) style['--r'] = `${radius}px`;
  return (
    <span className={`fk-hub-tile${outline ? ' fk-hub-tile-outline' : ''}`} style={style} aria-hidden="true">
      <span className="fk-hub-tile-h">H</span>
      <span className="fk-hub-tile-bar" />
    </span>
  );
}

/** Надпись «FISHKA HUB» и подпись под ней. */
export function HubWord({ size = 20, tagline = true, className }: { size?: number; tagline?: boolean; className?: string }): JSX.Element {
  const style: Vars = { '--w': `${size}px` };
  return (
    <span className={`fk-hub-word${className ? ` ${className}` : ''}`} style={style}>
      <span className="fk-hub-word-name">
        <span className="fk-hub-word-fishka">FISHKA</span>
        <span className="fk-hub-word-hub">HUB</span>
      </span>
      {tagline && <span className="fk-hub-word-tag">знания и обучение сети</span>}
    </span>
  );
}

/**
 * Размеры связки «знак + надпись»: md — шапка меню, lg — экраны входа.
 * Соотношения из «Nav bar lockup» макета (плитка 96 / надпись 40 / зазор 24,
 * скругление .25). Шапка меню — 264 px: 20 + 40 + 10 + ~125 (надпись 19 px)
 * — до кнопки «Свернуть меню» на границе остаётся ~55 px. Подпись не мельче
 * 10 px (в макете .26 от надписи) и уже «FISHKA HUB», как в макете.
 * В свёрнутом меню надпись прячет CSS (.fk-shell-collapsed), на телефоне
 * в шторке она видна всегда.
 */
const LOCKUP = {
  md: { tile: 40, radius: 10, word: 19, gap: 10 },
  lg: { tile: 56, radius: 14, word: 24, gap: 14 },
} as const;

/**
 * Знак + «FISHKA HUB» + «знания и обучение сети» (в свёрнутом виде — только знак).
 * `onDark` — на индиговом фоне: контурный знак и белые HUB и подпись.
 */
export function HubLogo({ compact, onDark, size = 'md' }: { compact?: boolean; onDark?: boolean; size?: keyof typeof LOCKUP }): JSX.Element {
  const l = LOCKUP[size];
  return (
    <span className={`fk-hub-logo${onDark ? ' fk-hub-logo-on-dark' : ''}`} style={{ gap: l.gap }}>
      <HubTile size={l.tile} radius={l.radius} outline={onDark} />
      {!compact && <HubWord size={l.word} className="fk-hub-logo-text" />}
    </span>
  );
}
